import { Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { AlertCircle, LoaderCircle, Lock, Pencil, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { canManageFactory } from "@/app/guards";
import { useObraId } from "@/app/useObraId";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ChipsRelacion } from "@/components/ui/ChipsRelacion";
import { DataTable } from "@/components/ui/data-table";
import { ExportButton } from "@/components/ui/ExportButton";
import { type CampoFiltro, FiltrosBar } from "@/components/ui/filtros-bar";
import { ImportButton } from "@/components/ui/ImportButton";
import { Modal } from "@/components/ui/modal";
import { ApiError } from "@/lib/api";
import { useMe } from "@/lib/auth";
import { useEquipos } from "@/lib/equipos";
import { AVISO_FILTRADO_LOCAL, mensajeErrorLista, useFiltrosURL } from "@/lib/filtros";
import { CLAVES_PUNTOS } from "@/lib/filtros/puntos";
import { useFuentes } from "@/lib/fuentes";
import {
  ordenarPuntos,
  type Punto,
  useCreatePunto,
  useDeletePunto,
  usePuntos,
  useUpdatePunto,
} from "@/lib/puntos";
import { PuntoForm, type PuntoFormValues } from "./PuntoForm";

// estadoLabel es la etiqueta del estado de SEGURIDAD del punto, binario desde #243: solo
// dice si algún candado sigue cubriéndolo. `activo` es un atributo del catálogo y va en su
// propia columna. La usan el accessor de la columna —del que tira la ordenación— y el badge
// de la celda; el filtro de estado de la FiltrosBar (del SERVIDOR, #247) rotula por su cuenta.
function estadoLabel(punto: Punto): string {
  return punto.bloqueado ? "Bloqueado" : "Desbloqueado";
}

// toInput normaliza los valores del formulario al payload del API. id_fuente 0 ⇒ null
// (automática: el servidor resuelve/auto-crea la fuente padre).
function toInput(values: PuntoFormValues) {
  return {
    id_equipo: values.id_equipo,
    id_fuente: values.id_fuente === 0 ? null : values.id_fuente,
    nombre: values.nombre.trim(),
    descripcion: values.descripcion.trim(),
    activo: values.activo,
  };
}

/**
 * PuntosPage lista los puntos de aislamiento con su equipo/fuente y ofrece el CRUD
 * completo. El servidor deriva `bloqueado` de los candados que siguen cubriendo el punto
 * (#243): los bloqueados se resaltan en rojo y se ordenan arriba, y el backend rechaza
 * renombrarlos, desactivarlos o borrarlos (409 PUNTO_EN_USO). La tabla usa el DataTable de
 * react-table (#34) SIN buscador en memoria (`sinBuscador`): el texto libre y el filtro por
 * estado son del SERVIDOR y viven en la FiltrosBar (#247), y la tabla solo ordena y pagina,
 * así que sus `accessorFn` alimentan la celda, la ordenación o ambas, nunca una búsqueda
 * (#346). Además importa xlsx (solo ADMIN).
 */
export function PuntosPage() {
  const { data: me } = useMe();
  const obraId = useObraId();
  const puedeGestionar = me != null && canManageFactory(me, obraId);

  // Los filtros viven en la URL (#247): compartible, recargable y con el servidor filtrando.
  const { filtros, setFiltros } = useFiltrosURL(CLAVES_PUNTOS);
  const {
    data: puntos,
    isPending,
    isError,
    baseVacia,
    avisoSinConexion,
    codigoError,
    camposError,
  } = usePuntos(obraId, filtros);
  const { data: equipos } = useEquipos(obraId);
  const { data: fuentes } = useFuentes(obraId);
  const crear = useCreatePunto(obraId);
  const editar = useUpdatePunto(obraId);
  const borrar = useDeletePunto(obraId);

  const [formState, setFormState] = useState<
    { mode: "nuevo" } | { mode: "editar"; punto: Punto } | null
  >(null);
  const [aBorrar, setABorrar] = useState<Punto | null>(null);

  const listaEquipos = equipos ?? [];
  const listaFuentes = fuentes ?? [];
  const sinEquipos = listaEquipos.length === 0;
  // Bloqueados arriba, luego por equipo/fuente/nombre (data-driven sobre `bloqueado`).
  const ordenados = puntos ? ordenarPuntos(puntos) : [];

  const columns = useMemo<ColumnDef<Punto, unknown>[]>(() => {
    const cols: ColumnDef<Punto, unknown>[] = [
      {
        accessorKey: "nombre",
        header: "Nombre",
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1.5 font-medium text-slate-900">
            {row.original.bloqueado ? (
              <Lock className="h-4 w-4 text-red-600" aria-label="Bloqueado" />
            ) : null}
            {row.original.nombre}
          </span>
        ),
      },
      {
        accessorKey: "descripcion",
        header: "Descripción",
        cell: ({ row }) => <span className="text-slate-900">{row.original.descripcion}</span>,
      },
      {
        id: "equipo",
        accessorFn: (p) => `${p.codigo_equipo} · ${p.nombre_equipo}`,
        header: "Equipo",
        cell: ({ row }) => (
          <Link
            to="/obras/$obraId/equipos"
            params={{ obraId: String(obraId) }}
            search={{ q: row.original.codigo_equipo }}
            aria-label={`Ver el equipo ${row.original.codigo_equipo}`}
            className="font-medium text-primary-700 hover:underline"
          >
            {row.original.codigo_equipo} · {row.original.nombre_equipo}
          </Link>
        ),
        meta: { stopRowClick: true },
      },
      {
        id: "fuente",
        accessorFn: (p) => `${p.codigo_fuente} · ${p.nombre_fuente}`,
        header: "Fuente",
        cell: ({ getValue }) => <span className="text-slate-500">{getValue<string>()}</span>,
        meta: { className: "hidden md:table-cell" },
      },
      // «Activo» es un ATRIBUTO del catálogo, no un estado (#243): columna propia.
      {
        id: "activo",
        accessorFn: (p) => (p.activo ? "Sí" : "No"),
        header: "Activo",
        cell: ({ getValue }) => <span className="text-slate-500">{getValue<string>()}</span>,
        meta: { className: "hidden md:table-cell" },
      },
      {
        id: "estado",
        accessorFn: (p) => estadoLabel(p),
        header: "Estado",
        cell: ({ row }) => (
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 font-medium text-sm ${
              row.original.bloqueado ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"
            }`}
          >
            {estadoLabel(row.original)}
          </span>
        ),
      },
      // Relación cruzada (#248): qué candados cubren el punto AHORA y desde qué LOTO. Cada
      // entidad conserva su enlace propio: el código abre el catálogo de candados ya filtrado
      // y el número de LOTO abre su ficha.
      {
        id: "candados",
        accessorFn: (p) => p.candados.map((c) => c.codigo_candado).join(" "),
        header: "Candados",
        meta: { stopRowClick: true },
        cell: ({ row }) => {
          const candados = row.original.candados;
          if (candados.length === 0) {
            return <span className="text-slate-500">Sin candados</span>;
          }
          return (
            <div className="flex min-w-44 flex-col gap-2">
              <div className="flex items-start gap-1.5">
                <span className="min-w-16 pt-0.5 text-slate-500 text-xs">Candados</span>
                <ChipsRelacion
                  vacio="Sin candados"
                  visibles={candados.length}
                  items={candados.map((candado) => ({
                    clave: candado.id_candado,
                    etiqueta: candado.codigo_candado,
                    titulo: `Ver el candado ${candado.codigo_candado}`,
                    enlace: {
                      to: "/obras/$obraId/candados",
                      params: { obraId: String(obraId) },
                      search: { q: candado.codigo_candado },
                    },
                  }))}
                />
              </div>
              <div className="flex items-start gap-1.5">
                <span className="min-w-16 pt-0.5 text-slate-500 text-xs">LOTOs</span>
                <ChipsRelacion
                  vacio="Sin LOTO"
                  visibles={candados.length}
                  items={candados.map((candado) => ({
                    clave: candado.id_bloqueo,
                    etiqueta: candado.numero_loto,
                    titulo: `Abrir ${candado.numero_loto}`,
                    enlace: {
                      to: "/obras/$obraId/lotos/$id",
                      params: { obraId: String(obraId), id: String(candado.id_loto) },
                    },
                  }))}
                />
              </div>
            </div>
          );
        },
      },
    ];
    if (puedeGestionar) {
      cols.push({
        id: "acciones",
        header: () => <span className="sr-only">Acciones</span>,
        enableSorting: false,
        enableGlobalFilter: false,
        meta: { className: "text-right", stopRowClick: true },
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Editar ${row.original.nombre}`}
              onClick={() => setFormState({ mode: "editar", punto: row.original })}
            >
              <Pencil className="h-5 w-5" aria-hidden="true" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Eliminar ${row.original.nombre}`}
              onClick={() => setABorrar(row.original)}
            >
              <Trash2 className="h-5 w-5 text-red-600" aria-hidden="true" />
            </Button>
          </div>
        ),
      });
    }
    return cols;
  }, [obraId, puedeGestionar]);

  // Campos de la barra de filtros. Los valores son los del API (ids y enums), no las
  // etiquetas; equipos y fuentes salen de los listados relacionados que la página ya carga.
  const campos = useMemo<CampoFiltro[]>(
    () => [
      { tipo: "texto", nombre: "q", etiqueta: "Buscar", placeholder: "Buscar puntos…" },
      {
        tipo: "select",
        nombre: "equipo",
        etiqueta: "Equipo",
        opciones: listaEquipos.map((e) => ({
          valor: String(e.id),
          etiqueta: `${e.codigo_equipo} · ${e.nombre_equipo}`,
        })),
      },
      {
        tipo: "select",
        nombre: "fuente",
        etiqueta: "Fuente",
        opciones: listaFuentes.map((f) => ({
          valor: String(f.id),
          etiqueta: `${f.codigo_fuente} · ${f.nombre_fuente}`,
        })),
      },
      {
        tipo: "select",
        nombre: "estado",
        etiqueta: "Estado",
        opciones: [
          { valor: "BLOQUEADO", etiqueta: "Bloqueado" },
          { valor: "DESBLOQUEADO", etiqueta: "Desbloqueado" },
        ],
      },
      { tipo: "booleano", nombre: "activo", etiqueta: "Activo" },
    ],
    [listaEquipos, listaFuentes],
  );

  function cerrarForm() {
    setFormState(null);
  }

  async function onSubmitForm(values: PuntoFormValues, addAnother: boolean) {
    if (formState?.mode === "editar") {
      await editar.mutateAsync({ id: formState.punto.id, input: toInput(values) });
      cerrarForm();
      return;
    }
    await crear.mutateAsync(toInput(values));
    // Chained-add: si se pulsó «Guardar y añadir otro», el modal permanece abierto (el
    // formulario limpia nombre/descripción manteniendo equipo y fuente); si no, se cierra.
    if (!addAnother) {
      cerrarForm();
    }
  }

  async function confirmarBorrado() {
    if (!aBorrar) {
      return;
    }
    try {
      await borrar.mutateAsync(aBorrar.id);
      setABorrar(null);
    } catch {
      // El fallo se refleja en borrar.error y se muestra en el modal, que se mantiene abierto.
    }
  }

  // Mensaje del error de borrado: el candado puesto (#243) se explica aparte del genérico.
  const mensajeBorrado =
    borrar.error instanceof ApiError && borrar.error.code === "PUNTO_EN_USO"
      ? "El punto tiene un candado puesto. Retira el candado antes de eliminarlo."
      : "No se pudo eliminar el punto. Inténtalo de nuevo.";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <h1 className="font-bold text-2xl text-slate-900 tracking-tight">
            Puntos de aislamiento
          </h1>
          <p className="text-base text-slate-500">
            Puntos donde se coloca el candado en cada fuente.
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <ImportButton tipo="puntos" etiqueta="puntos" obraId={obraId} />
          {/* La exportación arrastra los filtros de la URL (#249 D9). */}
          <ExportButton tipo="puntos" obraId={obraId} filtros={filtros} />
          {puedeGestionar ? (
            <Button onClick={() => setFormState({ mode: "nuevo" })} disabled={sinEquipos}>
              <Plus className="h-5 w-5" aria-hidden="true" />
              Nuevo punto
            </Button>
          ) : null}
        </div>
      </div>

      {puedeGestionar && sinEquipos ? (
        <Alert variant="info">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>Crea primero un equipo para poder dar de alta puntos de aislamiento.</span>
        </Alert>
      ) : null}

      {isError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>
            {mensajeErrorLista(
              { codigoError, camposError },
              "No se pudieron cargar los puntos. Inténtalo de nuevo.",
            )}
          </span>
        </Alert>
      ) : null}

      {isPending ? (
        <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
          <LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />
          Cargando puntos…
        </div>
      ) : null}

      {!isPending && !isError && baseVacia ? (
        <div className="rounded-2xl border border-slate-200 border-dashed bg-white px-4 py-12 text-center text-slate-500">
          Aún no hay puntos de aislamiento.
          {puedeGestionar && !sinEquipos ? " Crea el primero con «Nuevo punto»." : ""}
        </div>
      ) : null}

      {!baseVacia ? (
        <>
          <FiltrosBar
            campos={campos}
            valor={filtros}
            onChange={setFiltros}
            aviso={avisoSinConexion ? AVISO_FILTRADO_LOCAL : undefined}
          />
          {!isPending ? (
            <DataTable
              columns={columns}
              data={ordenados}
              sinBuscador
              onRowClick={
                puedeGestionar ? (punto) => setFormState({ mode: "editar", punto }) : undefined
              }
              rowClassName={(punto) => (punto.bloqueado ? "bg-red-50" : undefined)}
            />
          ) : null}
        </>
      ) : null}

      {/* Alta / edición */}
      <Modal
        open={formState !== null}
        onClose={cerrarForm}
        title={formState?.mode === "editar" ? "Editar punto" : "Nuevo punto"}
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto"
      >
        {formState !== null ? (
          <>
            {formState.mode === "editar" ? (
              <section aria-labelledby="candados-del-punto">
                <div className="flex items-center gap-2">
                  <Lock className="h-5 w-5 text-slate-500" aria-hidden="true" />
                  <h3 id="candados-del-punto" className="font-semibold text-slate-900">
                    Candados colocados
                  </h3>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600 text-sm">
                    {formState.punto.candados.length}
                  </span>
                </div>
                {formState.punto.candados.length === 0 ? (
                  <p className="mt-3 text-slate-500">Sin candados colocados</p>
                ) : (
                  <ul className="mt-3 flex flex-col gap-2">
                    {formState.punto.candados.map((candado) => (
                      <li
                        key={candado.id_bloqueo}
                        className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2"
                      >
                        <Link
                          to="/obras/$obraId/candados"
                          params={{ obraId: String(obraId) }}
                          search={{ q: candado.codigo_candado }}
                          className="font-semibold text-primary-700 hover:underline"
                        >
                          {candado.codigo_candado}
                        </Link>
                        <Link
                          to="/obras/$obraId/lotos/$id"
                          params={{ obraId: String(obraId), id: String(candado.id_loto) }}
                          className="text-right text-primary-700 text-sm hover:underline"
                        >
                          {candado.numero_loto}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ) : null}
            <PuntoForm
              equipos={listaEquipos}
              fuentes={listaFuentes}
              codigoFuente={formState.mode === "editar" ? formState.punto.codigo_fuente : undefined}
              showAddAnother={formState.mode === "nuevo"}
              defaultValues={
                formState.mode === "editar"
                  ? {
                      id_equipo: formState.punto.id_equipo,
                      id_fuente: formState.punto.id_fuente,
                      nombre: formState.punto.nombre,
                      descripcion: formState.punto.descripcion,
                      activo: formState.punto.activo,
                    }
                  : {
                      id_equipo: listaEquipos[0]?.id ?? 0,
                      id_fuente: 0,
                      nombre: "",
                      descripcion: "",
                      activo: true,
                    }
              }
              submitLabel={formState.mode === "editar" ? "Guardar cambios" : "Crear punto"}
              pending={crear.isPending || editar.isPending}
              onSubmit={onSubmitForm}
              onCancel={cerrarForm}
            />
          </>
        ) : null}
      </Modal>

      {/* Confirmación de borrado */}
      <Modal open={aBorrar !== null} onClose={() => setABorrar(null)} title="Eliminar punto">
        <p className="text-base text-slate-600">
          ¿Seguro que quieres eliminar el punto <strong>{aBorrar?.nombre}</strong>? Esta acción lo
          da de baja.
        </p>
        {borrar.isError ? (
          <Alert variant="destructive">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>{mensajeBorrado}</span>
          </Alert>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setABorrar(null)} disabled={borrar.isPending}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => void confirmarBorrado()}
            disabled={borrar.isPending}
          >
            {borrar.isPending ? (
              <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
            ) : (
              <Trash2 className="h-5 w-5" aria-hidden="true" />
            )}
            Eliminar
          </Button>
        </div>
      </Modal>
    </div>
  );
}
